import { ProcessingOverlay } from '@components'

import {
  AlignItems,
  BorderRadius,
  Color,
  FilesPreview,
  FileType,
  Overflow,
  SkeletonBox,
  Spacing,
} from '@billy/design-system'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DEFAULT_ZOOM_LEVEL } from '@features/invoices/constants/defaultZoomLevel'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { useInvoicePreview } from '@features/invoices/hooks/useInvoicePreview'
import { VatRatesContextProvider } from '@features/vatRates/contexts/vatRatesContext'

import { getClassName } from '../../../../utils/getClassName'
import { getInvoicingSettingsPreviewZoom } from '../../utils/getInvoicingSettingsPreviewZoom'
import { useInvoicingSettingsPreviewPayload } from './hooks/useInvoicingSettingsPreviewPayload'
import * as Styled from './styles'

const InvoicingSettingsPreviewDocument = (): ReactElement => {
  const { t } = useTranslation()
  const previewWrapperRef = useRef<HTMLDivElement>(null)
  const [zoomScale, setZoomScale] = useState(DEFAULT_ZOOM_LEVEL)
  const { invoiceDraftPayload } = useInvoicingSettingsPreviewPayload()
  const { htmlFiles, isLoading } = useInvoicePreview({
    documentType: InvoiceDocumentType.Invoice,
    invoiceDraftPayload,
  })

  const previewFiles = useMemo(
    () => htmlFiles?.map((file) => ({ alt: t('invoicing_settings.document_preview'), src: file })) ?? [],
    [htmlFiles, t],
  )

  useEffect(() => {
    const wrapperWidth = previewWrapperRef.current?.offsetWidth

    if (!wrapperWidth) {
      return
    }

    setZoomScale(getInvoicingSettingsPreviewZoom(wrapperWidth))
  }, [])

  return (
    <Styled.InvoicingSettingsPreviewWrapper ref={previewWrapperRef}>
      {previewFiles.length === 0 && <SkeletonBox fullHeight fullWidth />}
      {previewFiles.length > 0 && isLoading && (
        <ProcessingOverlay
          borderRadius={BorderRadius.R16}
          color={Color.White}
          label={t('invoicing_settings.updating_preview')}
          withAnimation
        />
      )}
      {previewFiles.length > 0 && (
        <FilesPreview
          alignment={AlignItems.SafeCenter}
          asType={FileType.Html}
          className={getClassName('dark-theme-suspend')}
          files={previewFiles}
          fitToWidth
          fullWidth={false}
          margin={Spacing.S16}
          withPagination={false}
          wrapperOverflowY={Overflow.Auto}
          zoomScale={zoomScale}
        />
      )}
    </Styled.InvoicingSettingsPreviewWrapper>
  )
}

// The preview reads the default rate through the context
export const InvoicingSettingsPreview = (): ReactElement => (
  <VatRatesContextProvider>
    <InvoicingSettingsPreviewDocument />
  </VatRatesContextProvider>
)
